<script>
    import {currencyFormatter} from '$utils/formatter.js';
    import {twMerge} from "tailwind-merge";
    import {t} from "$utils/lang.js";
    import {default as ShippingTruckIcon} from "$m7-library/component/Icons/ShippingTruck.svelte";
    import {default as ShippingExpressIcon} from "$m7-library/component/Icons/ShippingExpress.svelte";
    import {default as ShippingPickupIcon} from "$m7-library/component/Icons/ShippingPickup.svelte";
    import {computeDeliveryDate, formatDeliveryDate, formatDeliveryDistance} from "$utils/deliveryDate.js";
    import {onMount} from "svelte";

    const {
        method,
        selected = false,
        onselect = () => {},
    } = $props();

    const cls = $derived(twMerge(
        "w-full text-left p-4 rounded-lg border-2 transition-colors cursor-pointer flex items-center justify-between gap-3",
        selected
            ? "border-green bg-green/5"
            : "border-gray-102 bg-white hover:border-gray-103",
    ));

    const cost = $derived(method.cost ?? 0);
    const set = $derived(method.shippingSet ?? {});
    const deliveryType = $derived(set.deliveryType ?? '');
    const isExpress = $derived(deliveryType.startsWith('goexpress'));
    const isPickup = $derived(deliveryType === '');

    // Server-resolved DB-Text (oxdelivery.m7_delivery_message bzw.
    // _man_stock, sonst oxdeliveryset.m7_delivery_message). Kann HTML mit
    // .expressDayPlaceholder / .expressCountdownPlaceholder enthalten und
    // wird live-aktualisiert (mirrors legacy delivery.min.js Verhalten).
    const deliveryMessage = $derived(method.deliveryMessage ?? '');
    const deliveryOffset = $derived(method.deliveryOffset ?? 0);

    let messageHost = $state(null);
    let intervalId = null;

    function refreshPlaceholders() {
        if (!messageHost) return;
        const dayEls = messageHost.querySelectorAll('.expressDayPlaceholder');
        const cdEls  = messageHost.querySelectorAll('.expressCountdownPlaceholder');
        if (dayEls.length === 0 && cdEls.length === 0) return;

        const now = new Date();
        // Each placeholder may have its own data-type/data-offset override
        // (legacy supported per-element). Fallbacks come from the shipping
        // set's deliveryType + the server-supplied deliveryOffset.
        const renderFor = (el) => {
            const type   = el.dataset.type || deliveryType;
            const offset = Number(el.dataset.offset ?? deliveryOffset) || 0;
            return computeDeliveryDate(type, offset, now);
        };

        for (const el of dayEls) {
            const result = renderFor(el);
            if (!result) continue;
            el.textContent = formatDeliveryDate(result.deliveryDate, now);
            if (!el.dataset.offset) el.dataset.offset = String(deliveryOffset);
        }
        for (const el of cdEls) {
            const result = renderFor(el);
            if (!result) continue;
            const dist = result.countdownDate.getTime() - now.getTime();
            el.textContent = dist > 0 ? formatDeliveryDistance(dist) : '';
            if (!el.dataset.offset) el.dataset.offset = String(deliveryOffset);
        }
    }

    onMount(() => {
        refreshPlaceholders();
        intervalId = setInterval(refreshPlaceholders, 1000);
        return () => clearInterval(intervalId);
    });

    // Re-run when message HTML changes (e.g. switch from normal → man_stock,
    // or when basket offset shifts).
    $effect(() => {
        deliveryMessage; deliveryOffset; messageHost;
        // Microtask defer so {@html} has already been patched into the DOM
        Promise.resolve().then(refreshPlaceholders);
    });
</script>

<button class={cls} onclick={() => onselect(set.id)} type="button" data-shipping-option={set.id} data-select-nameed={selected} data-checkout-shipping-cost={cost}>
    <span class="flex items-center gap-3 min-w-0">
        <span class="shrink-0 size-5 rounded-full border-2 flex items-center justify-center"
              class:border-green={selected}
              class:border-gray-102={!selected}>
            {#if selected}
                <span class="size-2.5 rounded-full bg-green"></span>
            {/if}
        </span>
        {#if isExpress}
            <ShippingExpressIcon class="shrink-0 h-5 w-5"/>
        {:else if isPickup}
            <ShippingPickupIcon class="shrink-0 h-5 w-5"/>
        {:else}
            <ShippingTruckIcon class="shrink-0 h-5 w-5"/>
        {/if}
        <span class="flex flex-col min-w-0">
            <span class="font-bold text-xsm text-black">{set.title}</span>
            {#if deliveryMessage}
                <span class="text-xs text-gray-103 mt-0.5"
                      bind:this={messageHost}
                      data-shipping-delivery-day={set.id}
                      data-shipping-delivery-offset={deliveryOffset}>
                    {@html deliveryMessage}
                </span>
            {:else if set.description}
                <span class="text-xs text-gray-103">{set.description}</span>
            {/if}
        </span>
    </span>
    <span class="font-bold text-xsm text-black shrink-0">
        {#if cost === 0}
            {t('Kostenlos')}
        {:else}
            {currencyFormatter.format(cost)}
        {/if}
    </span>
</button>
